import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Heading,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Badge,
  Flex,
  Icon,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiActivity, FiFileText, FiArrowRight } from 'react-icons/fi';

const mockClaims = [
  {
    id: 'CLM-2026-0722-0148',
    dateOfService: '2026-07-22',
    status: 'Ready for Rules Engine',
    data: {
      type: '837P',
      patient: { first_name: 'Maria', last_name: 'Reyes', member_id: 'KF882044317' },
      provider: { name: 'David T. Nguyen, MD', npi: '1902847561' },
      claim: {
        total_charges: 186.0,
        payer: 'Keystone First (HealthChoices)',
        diagnoses: ['I10', 'E11.9'],
        procedures: [
          { cpt: 'T1015', charge: 186.0 },  // FQHC encounter — first claim line
          { cpt: '99214', charge: 0.0 },    // zero-pay tracking line
        ],
      },
    },
  },
  {
    id: 'CLM-2026-0722-0153',
    dateOfService: '2026-07-22',
    status: 'Ready for Rules Engine',
    data: {
      type: '837P',
      patient: { first_name: 'Walter', last_name: 'Brooks', member_id: 'IBX55H2274901' },
      provider: { name: 'Amara Okafor, CRNP', npi: '1730558492' },
      claim: {
        total_charges: 198.42,
        payer: 'Keystone 65 HMO (IBC Medicare Advantage)',
        payer_class: 'Medicare Advantage',   // dual-eligible: triggers Rev 0519 wrap computation
        diagnoses: ['E11.65', 'I50.32'],
        procedures: [
          { cpt: 'G0467', charge: 198.42 },  // FQHC visit, established patient (Medicare)
        ],
      },
    },
  },
  {
    id: 'CLM-2026-0721-0912',
    dateOfService: '2026-07-21',
    status: 'Ready for Rules Engine',
    data: {
      type: '837P',
      patient: { first_name: 'Linh', last_name: 'Pham', member_id: 'KF884402317' },
      provider: { name: 'David T. Nguyen, MD', npi: '1902847561' },
      claim: {
        total_charges: 186.0,
        payer: 'Keystone First (HealthChoices)',
        diagnoses: ['Z00.00'],
        procedures: [
          { cpt: 'T1015', charge: 186.0 },
          { cpt: '99385', charge: 0.0 },    // zero-pay tracking line
        ],
      },
    },
  },
];

const FqPaClaimsList = () => {
  const navigate = useNavigate();
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const theadBg = useColorModeValue('gray.50', 'gray.900');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  const handleAnalyze = (claimData) => {
    navigate('/fq-pa/claim/demo', { state: { claimData } });
  };

  return (
    <Box maxW="7xl" mx="auto">
      <Flex justify="space-between" align="flex-end" mb={8}>
        <Box>
          <Heading size="lg" mb={2}>Claims & Rules</Heading>
          <Text color="gray.500">
            Select a claim to run it through the FQHC Rules Engine and Eligibility checks.
          </Text>
        </Box>
      </Flex>

      <Box bg={bgColor} borderRadius="xl" borderWidth="1px" borderColor={borderColor} overflow="hidden" shadow="sm">
        <Table variant="simple">
          <Thead bg={theadBg}>
            <Tr>
              <Th>Claim ID</Th>
              <Th>Date of Service</Th>
              <Th>Patient</Th>
              <Th>Provider</Th>
              <Th isNumeric>Total Charges</Th>
              <Th>Status</Th>
              <Th></Th>
            </Tr>
          </Thead>
          <Tbody>
            {mockClaims.map((claim) => (
              <Tr key={claim.id} _hover={{ bg: hoverBg }}>
                <Td fontWeight="medium">
                  <Flex align="center">
                    <Icon as={FiFileText} mr={2} color="blue.500" />
                    {claim.id}
                  </Flex>
                </Td>
                <Td>{claim.dateOfService}</Td>
                <Td>{claim.data.patient.first_name} {claim.data.patient.last_name}</Td>
                <Td>{claim.data.provider.name}</Td>
                <Td isNumeric>${claim.data.claim.total_charges.toFixed(2)}</Td>
                <Td>
                  <Badge colorScheme="blue" variant="subtle" display="flex" alignItems="center" w="fit-content">
                    <Icon as={FiActivity} mr={1} />
                    {claim.status}
                  </Badge>
                </Td>
                <Td isNumeric>
                  <Button
                    size="sm"
                    colorScheme="blue"
                    rightIcon={<FiArrowRight />}
                    onClick={() => handleAnalyze(claim.data)}
                  >
                    Analyze Claim
                  </Button>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
};

export default FqPaClaimsList;